import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import useAuth from "./useAuth";

interface CalendarMeetingData {
  day: string; // YYYY-MM-DD format
  meeting_count: number;
}

interface CalendarActivity {
  date: string; // YYYY-MM-DD format
  count: number;
}

interface UseCalendarReturn {
  activities: CalendarActivity[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

interface UseCalendarProps {
  monthKey?: string; // Format: "YYYY-MM"
}

const useCalendar = ({
  monthKey,
}: UseCalendarProps = {}): UseCalendarReturn => {
  const [activities, setActivities] = useState<CalendarActivity[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { user_id } = useAuth();

  // Create the API date string (first day of month)
  const apiDateString = monthKey
    ? `${monthKey}-01`
    : `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(
        2,
        "0"
      )}-01`;

  const fetchCalendarData = useCallback(async () => {
    if (!user_id) {
      setError("User not authenticated");
      setIsLoading(false);
      return;
    }

    console.log("Fetching calendar data for:", {
      date: apiDateString,
      userId: user_id,
    });
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        "https://oparl-textprovider-production.up.railway.app/calendarSummary",
        //`http://localhost:1080/calendarSummary`,
        {
          params: {
            date: apiDateString,
            userId: user_id,
          },
          timeout: 10000, // 10 second timeout
        }
      );

      console.log("Calendar API response:", response.data);

      // Handle empty response or non-array response
      if (!response.data || !Array.isArray(response.data)) {
        console.warn(
          "Calendar API returned unexpected data format:",
          response.data
        );
        setActivities([]);
        return;
      }

      // Transform the API response to match Calendar component expectations
      const transformedData: CalendarActivity[] = response.data.map(
        (item: CalendarMeetingData) => ({
          date: item.day,
          count: item.meeting_count,
        })
      );

      setActivities(transformedData);
      console.log("Calendar activities updated:", transformedData);
    } catch (err) {
      console.error("Failed to fetch calendar data:", err);
      if (axios.isAxiosError(err)) {
        if (err.code === "ECONNABORTED") {
          setError("Request timeout - please try again");
        } else if (err.response?.status === 404) {
          setError("Calendar service not found");
        } else if (err.response?.status && err.response.status >= 500) {
          setError("Server error - please try again later");
        } else {
          setError(`Failed to load calendar data: ${err.message}`);
        }
      } else {
        setError("Failed to load calendar data");
      }
      setActivities([]);
    } finally {
      setIsLoading(false);
      console.log("Calendar loading finished");
    }
  }, [user_id, apiDateString]);

  useEffect(() => {
    console.log("useCalendar effect triggered", {
      user_id,
      monthKey,
      apiDateString,
    });
    if (!user_id) {
      setIsLoading(false);
      setError(null);
      setActivities([]);
      return;
    }
    fetchCalendarData();
  }, [fetchCalendarData, user_id, apiDateString, monthKey]);

  const refetch = () => {
    fetchCalendarData();
  };

  return {
    activities,
    isLoading,
    error,
    refetch,
  };
};

export default useCalendar;
